/**
 * Configuration Types
 *
 * Domain types for checkout configuration, market settings, and module activation.
 * These are simplified from the GraphQL types for easier use in the application.
 *
 * All types are derived from Zod schemas in ./schemas.ts to ensure
 * consistency between TypeScript types and OpenAPI documentation.
 *
 * @see lib/configuration/schemas.ts - Zod schemas (single source of truth)
 * @see lib/swagger/registry.ts - OpenAPI schema registration
 */

import type {
  ConfigCheckoutConfig,
  ConfigCountryInfo,
  ConfigLanguageInfo,
  ConfigModule,
  ConfigModuleActivation,
} from '@/graphql/configuration/api';

// Re-export all types from schemas.ts (single source of truth)
// Re-export schemas for validation use
export {
  CheckoutBundleSchema,
  CheckoutConfigSchema,
  CountryConfigSchema,
  CountryInfoSchema,
  LanguageInfoSchema,
  LocaleSchema,
  ModuleActivationSchema,
  PaymentLimitSchema,
  PaymentLimitsSchema,
  SupportedCountryInfoSchema,
} from './schemas';
export type {
  CheckoutBundle,
  CheckoutConfig,
  CountryInfo,
  LanguageInfo,
  Locale,
  ModuleActivation,
  PaymentLimit,
  PaymentLimits,
  SupportedCountryInfo,
} from './schemas';

// Import types from schemas for use in this file
import type {
  CheckoutConfig,
  CountryInfo,
  LanguageInfo,
  Locale,
  ModuleActivation,
  PaymentLimits,
  SupportedCountryInfo,
} from './schemas';

// Re-export the Module enum for convenience
export type Module = ConfigModule;

/**
 * Country configuration for routing and locale
 * Note: This is different from CountryInfo - this is used for locale/routing configuration
 */
export interface Country {
  code: string; // ISO country code (e.g., 'US')
  name: string; // Display name (e.g., 'United States')
  slug: string; // URL slug (e.g., 'us')
  channelSlug: string; // Saleor channel slug (e.g., 'us-usd')
  currency: string; // ISO currency code (e.g., 'USD')
  defaultLocale: string; // Default language locale id for country (e.g. 'en-GB', 'pt')
  locales: Locale[]; // Available locales
  continent: string; // Continent name for grouping
  favorite?: boolean; // Show in favorites section
  /** Live market flag from brand fallback JSON (`supportedCountriesInfo.supported`). */
  supported: boolean;
}

/**
 * State for locale configuration
 */
export interface LocaleConfigurationState {
  countries: Country[];
  languages: Record<string, Locale>;
  currentCountry?: Country | null;
  currentLocale?: Locale | null;
  isLoading?: boolean;
  error?: Error | null;
}

/**
 * Transform raw GraphQL CountryInfo to domain type
 */
export function transformCountryInfo(raw: ConfigCountryInfo): CountryInfo {
  return {
    code: raw.code,
    name: raw.name,
    region: raw.region,
    continent: raw.continent,
    supported: raw.supported,
    favorite: raw.favorite,
    languages: raw.languages,
    channel: raw.channel ?? null,
    currency: raw.currency ?? null,
  };
}

/**
 * Transform raw GraphQL LanguageInfo to domain type
 */
export function transformLanguageInfo(raw: ConfigLanguageInfo): LanguageInfo {
  return {
    code: raw.code,
    contentful: raw.contentful,
    locale: raw.locale,
    name: raw.name,
    slug: raw.slug,
    sfCode: raw.sfCode ?? null,
  };
}

/**
 * Transform raw GraphQL CountryInfo (with languageInfos) to SupportedCountryInfo
 */
export function transformSupportedCountryInfo(
  raw: ConfigCountryInfo & { languageInfos?: Array<ConfigLanguageInfo | null> | null }
): SupportedCountryInfo {
  return {
    ...transformCountryInfo(raw),
    languageInfos: (raw.languageInfos ?? [])
      .filter((l): l is ConfigLanguageInfo => l != null)
      .map(transformLanguageInfo),
  };
}

/**
 * Transform raw GraphQL CheckoutConfig to domain type
 *
 * Note: Split payment fields (maxPayments, firstPaymentMinPercentage, minOrderAmount,
 * paymentLinkDuration) are not queried due to API permission restrictions. These
 * fields require authenticated access that's only available in checkout context.
 */
export function transformCheckoutConfig(raw: ConfigCheckoutConfig): CheckoutConfig {
  // Cast to access fields that may or may not be present depending on query
  const rawAny = raw as Record<string, unknown>;
  return {
    country: raw.country ?? '',
    channel: raw.channel ?? null,
    returnPeriodDays: raw.returnPeriodDays ?? null,
    // Split payment fields - not currently queried due to permission restrictions
    maxPayments: (rawAny.maxPayments as number | null) ?? null,
    firstPaymentMinPercentage: (rawAny.firstPaymentMinPercentage as number | null) ?? null,
    minOrderAmount: (rawAny.minOrderAmount as number | null) ?? null,
    paymentLinkDuration: (rawAny.paymentLinkDuration as number | null) ?? null,
    bankDetails: raw.bankDetails ?? null,
    offlineBankTransferMinOrderAmount: raw.offlineBankTransferMinOrderAmount ?? null,
    offlineBankTransferWatchRequired: raw.offlineBankTransferWatchRequired ?? null,
    paymentLimits: {
      affirm: buildLimit(raw.affirmPaymentMin, raw.affirmPaymentMax),
      agos: buildLimit(raw.agosPaymentMin, raw.agosPaymentMax),
      applepayExpress: buildLimit(raw.applepayExpressPaymentMin, raw.applepayExpressPaymentMax),
      cembraFinancing: buildLimit(raw.cembraFinancingPaymentMin, raw.cembraFinancingPaymentMax),
      cembraOpenInvoice: buildLimit(
        raw.cembraOpenInvoicePaymentMin,
        raw.cembraOpenInvoicePaymentMax
      ),
      creditPlus: buildLimit(raw.creditPlusPaymentMin, raw.creditPlusPaymentMax),
      klarnaAccountFinancing: buildLimit(
        raw.klarnaAccountFinancingPaymentMin,
        raw.klarnaAccountFinancingPaymentMax
      ),
      klarnaPayLater: buildLimit(raw.klarnaPayLaterPaymentMin, raw.klarnaPayLaterPaymentMax),
      klarnaPayNow: buildLimit(raw.klarnaPayNowPaymentMin, raw.klarnaPayNowPaymentMax),
      oney3x: buildLimit(raw.oney3xPaymentMin, raw.oney3xPaymentMax),
      oney4x: buildLimit(raw.oney4xPaymentMin, raw.oney4xPaymentMax),
      oricoFinancing: buildLimit(raw.oricoFinancingPaymentMin, raw.oricoFinancingPaymentMax),
      paypalExpress: buildLimit(raw.paypalExpressPaymentMin, raw.paypalExpressPaymentMax),
      smbc: buildLimit(raw.smbcPaymentMin, raw.smbcPaymentMax),
      twint: buildLimit(raw.twintPaymentMin, raw.twintPaymentMax),
      v12: buildLimit(raw.v12PaymentMin, raw.v12PaymentMax),
      yourAsset: buildLimit(raw.yourAssetPaymentMin, raw.yourAssetPaymentMax),
      zip: buildLimit(raw.zipPaymentMin, raw.zipPaymentMax),
    },
  };
}

function buildLimit(
  min: number | null | undefined,
  max: number | null | undefined
): { min?: number; max?: number } | undefined {
  if (min == null && max == null) return undefined;
  const result: { min?: number; max?: number } = {};
  if (min != null) result.min = min;
  if (max != null) result.max = max;
  return result;
}

/**
 * Transform raw GraphQL ModuleActivation to domain type
 */
export function transformModuleActivation(raw: ConfigModuleActivation): ModuleActivation {
  const modules: Record<string, boolean> = {};
  for (const config of raw.configs) {
    modules[config.module] = config.active;
  }
  return {
    country: raw.country,
    modules,
  };
}

/**
 * Check if a module is active for a given activation config
 */
export function isModuleActive(activation: ModuleActivation | null, module: Module): boolean {
  if (!activation) return false;
  return activation.modules[module] ?? false;
}

/**
 * Like `isModuleActive`, but treats "the service did not tell us" as active.
 *
 * `isModuleActive` collapses three different states into `false`: the module is
 * switched off, the country is absent from the response, and the query failed
 * altogether. That is the right default for a payment method — an unknown
 * gateway must not be offered — but it is the wrong one for a module that gates
 * a page's primary call to action, because a configuration outage would then
 * silently strip that CTA from every market at once.
 *
 * Use this where the module can only ever *remove* something the storefront
 * already shows, so the failure mode is "no worse than before the gate existed"
 * rather than "the page loses its CTA". An explicit `active: false` still hides
 * it; only silence is read as a yes.
 */
export function isModuleActiveOrUnknown(
  activation: ModuleActivation | null,
  module: Module
): boolean {
  return activation?.modules[module] ?? true;
}

/**
 * Get payment limit for a specific method
 */
export function getPaymentLimit(
  config: CheckoutConfig | null,
  method: keyof PaymentLimits
): { min?: number; max?: number } | undefined {
  if (!config) return undefined;
  return config.paymentLimits[method];
}
